import { action, observable } from 'mobx';
import { Unsubscribe } from 'router5/dist/types/base';
import { Container, Inject } from 'typescript-ioc';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { IEventHubDTO, IEventHubsDAO } from '../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../api/implementations/event-hubs.dao';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../constants/common-labels.constants';
import { ISubWindowsService } from '../../../infrastructure/sub-windows';
import { HubOrganizationForm } from './_hub-organization-form/hub-organization-form';
import { HubConfigurationVM } from './_hub-configuration/hub-configuration.viewmodel';
import { EventHubAttendeesViewModel } from './_event-hub-attendees/event-hub-attendees.viewmodel';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';


export enum EventHubActiveTab {
    HubConfiguration,
    Attendees
}

export class ManageEventHubVM {
    @observable
    private activeTab: EventHubActiveTab;
    @observable
    private eventHub: IEventHubDTO;
    @observable
    private loading: boolean = false;
    @observable
    private isManageUrlModalOpen: boolean = false;
    private hubConfigurationVM: HubConfigurationVM | null = null;
    private attendeesVM: EventHubAttendeesViewModel | null = null;

    private subscriptions: Unsubscribe[];

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly routingService: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly subWindowsService: ISubWindowsService = Container.get(ISubWindowsService),
    ) {
    }

    public async initialize(): Promise<void> {
        this.initRouting();
        this.redirectIfNotPermitted();
        await this.loadEventHubData();
    }

    public shutdown(): void {
        this.subscriptions.forEach(unsubscribe => unsubscribe());
    }

    public getActiveTab(): EventHubActiveTab {
        return this.activeTab;
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public getAccountId(): number {
        return this.accountId;
    }

    @action
    public async loadEventHubData(): Promise<void> {
        this.startLoading();
        try {
            const response = await this.eventHubsDAO.getEventHub(this.accountId, this.eventHubId);
            this.setEventHub(response);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.stopLoading();
        }
    }

    public getEventHub(): IEventHubDTO {
        return this.eventHub;
    }

    public getHubConfigurationVM(): HubConfigurationVM {
        if (!this.hubConfigurationVM) {
            this.hubConfigurationVM = new HubConfigurationVM(
                this.accountId,
                this.eventHubId,
                this.eventHub?.eventHubName
            );
        }
        return this.hubConfigurationVM;
    }

    public getAttendeesVM(): EventHubAttendeesViewModel {
        if (!this.attendeesVM) {
            this.attendeesVM = new EventHubAttendeesViewModel(this.accountId, this.eventHubId);
        }
        return this.attendeesVM;
    }

    public getIsManageUrlModalOpen(): boolean {
        return this.isManageUrlModalOpen;
    }

    @action
    public openManageUrlModal = (): void => {
        this.isManageUrlModalOpen = true;
    };

    @action
    public closeManageUrlModal = (): void => {
        this.isManageUrlModalOpen = false;
    };

    @action
    public openNewOrgPopup = (): void => {
        this.subWindowsService.showSubWindow({
            title: 'New Organization',
            componentToHost: HubOrganizationForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                updateOrganizationOnList: () => {
                    // Refresh any organization lists if needed
                },
            },
        });
    };

    public openHubWebsiteBuilder = (): void => {
        this.routingService.navigate(Routes.EventHubWebsiteBuilder, {
            accountId: String(this.accountId),
            eventHubId: String(this.eventHubId)
        });
    };

    private initRouting(): void {
        this.subscriptions = [
            this.routingService.listen(Routes.EventHubAttendees, () =>
                this.handleTabRouteChange(Permission.VIEW_HUB_ATTENDEES, EventHubActiveTab.Attendees)
            ),
            this.routingService.listen(Routes.EventHubConfiguration, () =>
                this.handleTabRouteChange(Permission.EVENT_HUBS_TAB, EventHubActiveTab.HubConfiguration)
            ),
        ];
    }

    private handleTabRouteChange(permission: Permission, tab: EventHubActiveTab): void {
        if (hasPermission(permission)) {
            this.setActiveTab(tab);
        } else {
            this.navigateToFirstAvailableTab();
        }
    }

    private navigateToFirstAvailableTab(): void {
        const fallback = this.getFirstAvailableTab();
        if (fallback) {
            this.routingService.navigate(fallback.route, { accountId: this.accountId, eventHubId: this.eventHubId });
        }
    }

    private redirectIfNotPermitted(): void {
        const { name } = this.routingService.router.getState();

        const isAttendeesRestricted = name === Routes.EventHubAttendees && !hasPermission(Permission.VIEW_HUB_ATTENDEES);
        const isConfigurationRestricted = name === Routes.EventHubConfiguration && !hasPermission(Permission.EVENT_HUBS_TAB);

        if (isAttendeesRestricted || isConfigurationRestricted) {
            this.navigateToFirstAvailableTab();
        }
    }

    private getFirstAvailableTab(): { route: Routes; tab: EventHubActiveTab } | null {
        if (hasPermission(Permission.VIEW_HUB_ATTENDEES)) {
            return { route: Routes.EventHubAttendees, tab: EventHubActiveTab.Attendees };
        }
        if (hasPermission(Permission.EVENT_HUBS_TAB)) {
            return { route: Routes.EventHubConfiguration, tab: EventHubActiveTab.HubConfiguration };
        }
        return null;
    }

    @action
    private setActiveTab(tab: EventHubActiveTab): void {
        this.activeTab = tab;
    }

    @action.bound
    private setEventHub(eventHub: IEventHubDTO): void {
        this.eventHub = eventHub;
    }

    @action
    private startLoading(): void {
        this.loading = true;
    }

    @action
    private stopLoading(): void {
        this.loading = false;
    }

}